import moment from "moment";
import { Datepicker, DateRangePicker } from "vanillajs-datepicker";
import { type DatepickerOptionsOrientation, DatepickerOptions } from "vanillajs-datepicker/Datepicker";
import { Behavior } from '@dia-ui/abstract';

/**
 * based on https://www.npmjs.com/package/vanillajs-datepicker
 */
@Behavior('date-picker')
export class DatePicker {
  element: any;

  name: string;
  default: string | Date | number;
  container: any;
  format: string;
  position: DatepickerOptionsOrientation;
  trigger: any;
  range: string;
  maxDate: string | Date | number;
  autoHide: string;

  static INSTANCE: {
    [name: string]: Datepicker | DateRangePicker
  } = {};

  // This is called only once, given the `element` that the behavior is attached to.
  constructor(element) {
    this.element = element;
  }

  // This is called any time the associated `element` is appended into the
  // DOM, passed in the `element`
  async connectedCallback(){
    this.name = this.element.getAttribute("name");
    this.default = this.element.getAttribute("data-date-picker-default") || Date.now();
    this.container = this.element.parentElement.querySelector("[data-date-picker-container]")
        || (this.element.parentElement.nodeName === "DIA-FORM-CONTROL" ? this.element.parentElement : undefined)
        || undefined;
    this.format = this.element.getAttribute("data-date-picker-format") || "DD.MM.YYYY";
    this.position = this.element.getAttribute("data-date-picker-position") || "bottom left";
    this.trigger = this.element.parentElement.parentElement.querySelectorAll("[data-date-picker-trigger]") || false;
    this.range = this.element.getAttribute("data-date-picker-range") || undefined;
    this.maxDate = this.element.getAttribute("data-date-picker-max-date") || Date.now();
    this.autoHide = this.element.getAttribute("data-date-picker-auto-hide") || true;

    if (this.range) {
      this.initDatepickerRange();
    } else if(!this.range) {
      this.initDatepicker();
    }

    /**
     * if we have trigger elements, we iterate theme and create relative event listeners
     * to show datepicker on click
     */
    if (this.trigger) {
      this.trigger.forEach((triggerElement) => {
        triggerElement.addEventListener("click", (event) => {
          let triggerFieldName = triggerElement.getAttribute("data-date-picker-trigger");
          event.target.parentElement.parentElement.querySelector('input[name="' + triggerFieldName + '"]').focus();
        });
      });
    }

    let changeDateListener = (event: CustomEvent): void => {
      event.target.dispatchEvent(new Event("change", { bubbles: true }));
    };

    this.element.removeEventListener("changeDate", changeDateListener);
    this.element.addEventListener("changeDate", changeDateListener);
  }

  /**
   * initDatepicker
   */
  initDatepicker() {
    DatePicker.INSTANCE[this.name] = new Datepicker(
      this.element,
      this.getConfig()
    );
  }

  /**
   * initDatepickerRange
   */
  initDatepickerRange() {
    if (this.range !== "true" && this.range?.length > 0) {
      let rangeElement= this.element.parentElement.parentElement.querySelector('input[name="' + this.range + '"]'),
        name= rangeElement.getAttribute("name");

      DatePicker.INSTANCE[this.name + name] = new DateRangePicker(
        this.element.parentElement.parentElement,
        {
          ...this.getConfig()
        }
      );
    }
  }

  /**
   * getConfig
   */
  getConfig(): DatepickerOptions {
    let format = this.format;

    return {
      container: this.container,
      format: {
        toValue(date: string): number {
          return moment(date).toDate().getTime();
        },
        toDisplay(date: Date): string {
          return moment(date).format(format);
        }
      },
      showOnClick: !this.trigger,
      orientation: this.position,
      defaultViewDate: this.default,
      updateOnBlur: false,
      maxDate: this.maxDate,
      autohide: this.autoHide !== "false"
    };
  }

  // This is called any time the associated `element` is removed from the DOM,
  // passed in the `element`.
  disconnectedCallback() {
  }

  // As with custom elements, define which attributes of the associated
  // element that the behavior should react to.
  /*static observedAttributes = [
    'data-draggable-events',
    'data-draggable-selector'
  ];*/

  // This is called any time any of the `observedAttributes` of the associated
  // element have been changed, just like with Custom Elements but with the
  // additional passed in `element`.
  // attributeChangedCallback(attributeName, oldValue, newValue, element) {}

  // There is one additional API, unlike with Custom Elements. If `static
  // awaitElementDefined` is `true`, then the behavior will not be
  // instantiated and connected until its host element is defined and upgraded
  // (that is, if the host element is possibly a custom element, having a
  // hyphen in its name). If the host element has no hyphen in its name, then
  // this does not apply, and the behavior will be created and connected
  // immediately without waiting. If a possibly-custom element is removed
  // before it is defined, then a behavior will not be created and connected
  // at all (waiting will have been canceled).
  static awaitElementDefined = true // Default is false.
}
